/**
 * PublicAgentProfilePage — Sous-lot E (light theme)
 *
 * Hiérarchie fondateur :
 *  1. Identité + photo (au-dessus de la ligne de flottaison)
 *  2. Métier, agence
 *  3. Territoire principal + positionnement
 *  4. CTA principal unique « Présenter mon projet »
 *  5. Synthèse de confiance (lecture rapide → approfondir)
 *  6. Avis vendeurs vérifiés
 *  7. Preuves professionnelles
 *  8. Publications locales utiles (3 max)
 *  9. Parcours, spécialités, zones
 * 10. Actions secondaires (suivre, partager, signaler)
 *
 * Aucun composant cockpit dark. Tout est en design system Réseau Light.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
    MapPin, Building2, Star, ShieldCheck, ChevronRight, Share2, Flag,
    Phone, Mail, Loader2, Sparkles, CheckCircle2, X, ArrowRight, MessageSquare,
    Calendar, Languages, Briefcase
} from 'lucide-react';

// ============================================================================
// Photo / Avatar
// ============================================================================
function AgentAvatar({ profile, size = 'lg' }) {
    const initials = (profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`)
        .split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
    const dim = size === 'xl' ? 'w-32 h-32 text-3xl' :
                size === 'lg' ? 'w-24 h-24 text-2xl' : 'w-12 h-12 text-base';
    const url = profile.photo_url
        ? (profile.photo_url.startsWith('http') ? profile.photo_url : `/api/users/photo/${profile.photo_url}`)
        : null;
    return (
        <div className={`${dim} rounded-2xl flex items-center justify-center font-bold flex-shrink-0 overflow-hidden`}
             style={{ background: 'linear-gradient(135deg, var(--reseau-violet-soft), var(--reseau-orange-soft))',
                      color: 'var(--reseau-violet)',
                      border: '2px solid var(--reseau-surface)',
                      boxShadow: 'var(--reseau-shadow-md)' }}
             data-testid="agent-avatar">
            {url ? <img src={url} alt="" className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }} /> : initials}
        </div>
    );
}

// ============================================================================
// Modale CTA — point d'entrée unique « Présenter mon projet »
// ============================================================================
function SellerIntentModal({ agent, open, onClose, onSubmitted }) {
    const [step, setStep] = useState('choice'); // choice → form → success
    const [intentType, setIntentType] = useState('');
    const [form, setForm] = useState({
        seller_first_name: '', seller_email: '', seller_phone: '', message: ''
    });
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const INTENT_CHOICES = [
        { value: 'estimate', label: 'Faire estimer mon bien', hint: 'Connaître la valeur de mon logement' },
        { value: 'sell_property', label: 'Vendre dans les prochains mois', hint: 'Démarrer un projet de vente' },
        { value: 'callback', label: 'Être rappelé', hint: 'Un échange téléphonique avant de décider' },
        { value: 'question', label: 'Poser une question simple', hint: 'Conseil ou information locale' },
    ];

    const submit = async () => {
        if (!form.seller_first_name.trim() || !form.seller_email.trim()) {
            toast.error('Prénom et email sont nécessaires');
            return;
        }
        if (!consent) {
            toast.error('Merci d\'accepter le partage de vos coordonnées avec cet agent.');
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/public/seller-intents', {
                professional_profile_id: agent.id,
                source: 'agent_profile',
                source_context: { page: `/p/agent/${agent.slug || agent.id}` },
                intent_type: intentType,
                consent_contact: true,
                consent_privacy: true,
                ...form,
                seller_last_name: '',
            });
            setStep('success');
            onSubmitted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Envoi impossible. Réessayez dans un instant.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div data-reseau="light" className="reseau-light fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
             style={{ background: 'var(--reseau-surface-overlay)' }}
             onClick={onClose}>
            <div className="reseau-card w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
                 style={{ borderRadius: 'var(--reseau-radius-xl) var(--reseau-radius-xl) 0 0' }}
                 onClick={e => e.stopPropagation()}
                 data-testid="seller-intent-modal">
                <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between mb-1">
                        <div>
                            <p className="reseau-eyebrow" style={{ color: 'var(--reseau-orange-strong)' }}>Étape {step === 'choice' ? '1' : step === 'success' ? '✓' : '2'} sur 2</p>
                            <h2 className="reseau-h2 text-xl sm:text-2xl mt-1">
                                {step === 'success' ? 'Demande transmise' : `Présenter mon projet à ${agent.first_name || 'cet agent'}`}
                            </h2>
                        </div>
                        <button onClick={onClose} className="reseau-btn-ghost p-2 rounded-full" aria-label="Fermer" data-testid="modal-close-btn">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <p className="text-sm mb-5" style={{ color: 'var(--reseau-ink-muted)' }}>
                        {step === 'choice' && 'Pas d\'engagement. Vos coordonnées ne sont partagées qu\'avec cet agent.'}
                        {step === 'form' && 'Vous restez maître de la suite : aucun appel automatique, aucun email commercial.'}
                        {step === 'success' && `${agent.first_name || "L'agent"} reviendra vers vous sous 24 à 48h. Aucun email automatique n'a été envoyé à un tiers.`}
                    </p>

                    {step === 'choice' && (
                        <div className="space-y-2" data-testid="intent-choice-list">
                            {INTENT_CHOICES.map(c => (
                                <button key={c.value}
                                        data-testid={`intent-choice-${c.value}`}
                                        onClick={() => { setIntentType(c.value); setStep('form'); }}
                                        className="w-full text-left p-4 reseau-card reseau-card-hoverable flex items-center justify-between">
                                    <div>
                                        <p className="font-semibold" style={{ color: 'var(--reseau-ink)' }}>{c.label}</p>
                                        <p className="text-xs mt-0.5" style={{ color: 'var(--reseau-ink-muted)' }}>{c.hint}</p>
                                    </div>
                                    <ArrowRight className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 'form' && (
                        <div className="space-y-3" data-testid="intent-form">
                            <div>
                                <label className="text-xs font-medium" style={{ color: 'var(--reseau-ink-soft)' }}>Prénom *</label>
                                <input className="reseau-input mt-1" data-testid="form-first-name"
                                       value={form.seller_first_name}
                                       onChange={e => setForm({ ...form, seller_first_name: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-medium" style={{ color: 'var(--reseau-ink-soft)' }}>Email *</label>
                                <input type="email" className="reseau-input mt-1" data-testid="form-email"
                                       value={form.seller_email}
                                       onChange={e => setForm({ ...form, seller_email: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-medium" style={{ color: 'var(--reseau-ink-soft)' }}>Téléphone (optionnel)</label>
                                <input className="reseau-input mt-1" data-testid="form-phone"
                                       value={form.seller_phone}
                                       onChange={e => setForm({ ...form, seller_phone: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-medium" style={{ color: 'var(--reseau-ink-soft)' }}>Quelques mots sur votre projet (optionnel)</label>
                                <textarea className="reseau-input mt-1" rows={3} data-testid="form-message"
                                          placeholder="Ex : T3 dans le 6e, vente envisagée d'ici l'été."
                                          value={form.message}
                                          onChange={e => setForm({ ...form, message: e.target.value })} />
                            </div>
                            <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="form-consent-label">
                                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                                       className="mt-1 accent-purple-700" data-testid="form-consent" />
                                <span className="text-xs" style={{ color: 'var(--reseau-ink-soft)' }}>
                                    J&apos;accepte que mes coordonnées soient partagées avec <strong>{agent.first_name || 'cet agent'}</strong> uniquement,
                                    et je reconnais avoir pris connaissance de la <Link to="/privacy" className="underline" style={{ color: 'var(--reseau-violet)' }}>politique de confidentialité</Link>.
                                </span>
                            </label>
                            <p className="text-xs mt-1 ml-6" style={{ color: 'var(--reseau-ink-muted)' }}>
                                <Link to="/protection-des-donnees" className="underline hover:no-underline" data-testid="data-prot-link-intent">
                                    Comment Homly protège mes données
                                </Link>
                            </p>
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setStep('choice')} className="reseau-btn reseau-btn-ghost flex-1" data-testid="form-back-btn">
                                    Retour
                                </button>
                                <button onClick={submit} disabled={submitting || !consent} data-testid="form-submit-btn"
                                        className="reseau-btn reseau-btn-primary flex-1">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer ma demande'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'success' && (
                        <div className="text-center py-6" data-testid="intent-success">
                            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                                 style={{ background: 'var(--reseau-success-soft)' }}>
                                <CheckCircle2 className="w-8 h-8" style={{ color: 'var(--reseau-success)' }} />
                            </div>
                            <p style={{ color: 'var(--reseau-ink-soft)' }}>
                                Vous pouvez fermer cette fenêtre — l&apos;agent reviendra vers vous très rapidement.
                            </p>
                            <button onClick={onClose} className="reseau-btn reseau-btn-primary mt-5">Continuer</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Synthèse de confiance simplifiée
// ============================================================================
function TrustSnapshot({ profileId, onCtaClick }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!profileId) return;
        api.get(`/public/trust-summary/${profileId}`)
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [profileId]);

    if (loading) return <div className="reseau-skeleton h-32 rounded-2xl" />;
    if (!data) return null;

    const signals = (data.signals || []).slice(0, 3);

    return (
        <section className="reseau-card p-5 sm:p-6" data-testid="trust-snapshot">
            <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5" style={{ color: 'var(--reseau-violet)' }} />
                <h2 className="reseau-h2 text-lg">Pourquoi lui faire confiance ?</h2>
            </div>
            {data.synthesis && (
                <p className="text-sm sm:text-base mb-4" style={{ color: 'var(--reseau-ink-soft)' }}>
                    {data.synthesis}
                </p>
            )}
            {signals.length > 0 && (
                <ul className="space-y-2 mb-4">
                    {signals.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--reseau-ink-soft)' }}>
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--reseau-success)' }} />
                            <span>{s.label || s.text}</span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <button onClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}
                        className="reseau-btn-text text-sm" data-testid="trust-explain-link">
                    Comprendre ces éléments →
                </button>
                <button onClick={onCtaClick} className="reseau-btn reseau-btn-accent" data-testid="trust-cta-btn">
                    Présenter mon projet
                </button>
            </div>
        </section>
    );
}

// ============================================================================
// Avis vendeurs (light)
// ============================================================================
function PublicReviews({ profileId }) {
    const [reviews, setReviews] = useState([]);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (!profileId) return;
        api.get(`/public/seller-reviews/by-profile/${profileId}?limit=5`)
            .then(r => setReviews(r.data?.items || []))
            .catch(() => {});
    }, [profileId]);

    if (reviews.length === 0) return null;

    return (
        <section id="reviews-section" className="reseau-card p-5 sm:p-6" data-testid="public-reviews">
            <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5" style={{ color: 'var(--reseau-orange)' }} />
                <h2 className="reseau-h2 text-lg">Avis vendeurs vérifiés</h2>
                <span className="reseau-badge reseau-badge-violet ml-auto text-xs">100% vérifiés</span>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--reseau-ink-muted)' }}>
                Chaque avis est lié à un mandat de vente réel. Vendeurs anonymisables.
            </p>
            <div className="space-y-3">
                {reviews.slice(0, 4).map(r => {
                    const dims = r.dimensions || r.ratings || {};
                    const dimKeys = Object.keys(dims);
                    // Moyenne pour afficher une note synthétique non-officielle
                    const avg = dimKeys.length > 0
                        ? dimKeys.reduce((s, k) => s + (Number(dims[k]) || 0), 0) / dimKeys.length
                        : null;
                    // Top 2 dimensions par défaut
                    const topDims = dimKeys
                        .map(k => ({ key: k, val: Number(dims[k]) || 0 }))
                        .sort((a, b) => b.val - a.val)
                        .slice(0, 2);
                    return (
                        <article key={r.id} className="reseau-card-soft p-4" data-testid={`review-${r.id}`}>
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <div>
                                    <p className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                        {r.public_display_name || r.seller_display_name || 'Vendeur vérifié'}
                                    </p>
                                    <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        {r.created_at_year_month
                                            ? new Date(r.created_at_year_month + '-01').toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
                                            : (r.submitted_at && new Date(r.submitted_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' }))}
                                    </p>
                                </div>
                                {avg !== null && (
                                    <div className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <Star key={n} className="w-4 h-4"
                                                  style={{
                                                      color: n <= Math.round(avg) ? 'var(--reseau-orange)' : 'var(--reseau-ink-faint)',
                                                      fill: n <= Math.round(avg) ? 'var(--reseau-orange)' : 'none'
                                                  }} />
                                        ))}
                                    </div>
                                )}
                            </div>
                            {(r.free_comment || r.comment) && (
                                <p className="text-sm italic" style={{ color: 'var(--reseau-ink-soft)' }}>
                                    « {r.free_comment || r.comment} »
                                </p>
                            )}
                            {topDims.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {topDims.map(d => (
                                        <span key={d.key} className="reseau-badge reseau-badge-violet text-xs">
                                            {d.key.replace(/_/g, ' ')} · {d.val}/5
                                        </span>
                                    ))}
                                    {dimKeys.length > 2 && (
                                        <button onClick={() => setExpanded({ ...expanded, [r.id]: !expanded[r.id] })}
                                                className="reseau-btn-text text-xs"
                                                data-testid={`review-${r.id}-toggle`}
                                                style={{ padding: '0 4px' }}>
                                            {expanded[r.id] ? 'Replier' : `+${dimKeys.length - 2} autres dimensions`}
                                        </button>
                                    )}
                                </div>
                            )}
                            {expanded[r.id] && (
                                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                    {Object.entries(dims).map(([key, val]) => (
                                        <div key={key} className="flex items-center justify-between">
                                            <span style={{ color: 'var(--reseau-ink-muted)' }}>{key.replace(/_/g, ' ')}</span>
                                            <span className="font-semibold" style={{ color: 'var(--reseau-ink)' }}>{val}/5</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {r.agent_response && (
                                <div className="mt-3 pt-3 border-t text-sm" style={{ borderColor: 'var(--reseau-border)' }}>
                                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--reseau-violet)' }}>Réponse du professionnel</p>
                                    <p style={{ color: 'var(--reseau-ink-soft)' }}>{r.agent_response}</p>
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

// ============================================================================
// Preuves professionnelles (light)
// ============================================================================
function PublicProofs({ profileId }) {
    const [proofs, setProofs] = useState([]);

    useEffect(() => {
        if (!profileId) return;
        api.get(`/public/proofs/by-profile/${profileId}`)
            .then(r => setProofs(r.data?.items || []))
            .catch(() => {});
    }, [profileId]);

    if (proofs.length === 0) return null;

    // Regroupement par catégorie
    const grouped = proofs.reduce((acc, p) => {
        const cat = p.category || 'activity';
        (acc[cat] = acc[cat] || []).push(p);
        return acc;
    }, {});

    const CAT_LABELS = {
        identity: { label: 'Identité professionnelle', icon: ShieldCheck },
        local: { label: 'Connaissance locale', icon: MapPin },
        activity: { label: 'Activité et expérience', icon: Briefcase },
    };

    const formatValue = (p) => {
        if (p.value == null) return null;
        if (p.unit === 'EUR') return (p.value / 1000).toFixed(0) + ' k€';
        if (p.unit === 'count') return `${p.value}`;
        return `${p.value}${p.unit ? ' ' + p.unit : ''}`;
    };

    return (
        <section className="reseau-card p-5 sm:p-6" data-testid="public-proofs">
            <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--reseau-success)' }} />
                <h2 className="reseau-h2 text-lg">Repères vérifiés</h2>
            </div>
            <div className="space-y-4">
                {Object.entries(grouped).map(([cat, items]) => {
                    const meta = CAT_LABELS[cat] || CAT_LABELS.activity;
                    const Icon = meta.icon;
                    return (
                        <div key={cat}>
                            <div className="flex items-center gap-2 mb-2">
                                <Icon className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                <h3 className="text-sm font-semibold uppercase tracking-wider"
                                    style={{ color: 'var(--reseau-ink-muted)', letterSpacing: '0.05em' }}>
                                    {meta.label}
                                </h3>
                            </div>
                            <ul className="space-y-1.5">
                                {items.map(p => (
                                    <li key={p.id} className="flex items-center justify-between text-sm py-1.5"
                                        data-testid={`proof-${p.proof_type}`}>
                                        <span style={{ color: 'var(--reseau-ink-soft)' }}>{p.title}</span>
                                        {formatValue(p) && (
                                            <span className="font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                                {formatValue(p)}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

// ============================================================================
// Publications locales utiles (max 3)
// ============================================================================
function LocalContent({ profileId }) {
    const [posts, setPosts] = useState([]);
    useEffect(() => {
        if (!profileId) return;
        api.get(`/public/profiles/${profileId}/posts?limit=3`)
            .then(r => setPosts(r.data?.items || []))
            .catch(() => {});
    }, [profileId]);

    if (posts.length === 0) return null;

    return (
        <section className="reseau-card p-5 sm:p-6" data-testid="local-content">
            <h2 className="reseau-h2 text-lg mb-4">Contenus utiles</h2>
            <div className="space-y-3">
                {posts.slice(0, 3).map(p => (
                    <article key={p.id} className="reseau-card-soft p-4 reseau-card-hoverable" data-testid={`local-post-${p.id}`}>
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--reseau-violet)' }}>
                            {p.category_label || p.category?.replace(/_/g, ' ')}
                        </p>
                        <h3 className="font-semibold mb-1" style={{ color: 'var(--reseau-ink)' }}>{p.title}</h3>
                        <p className="text-sm line-clamp-2" style={{ color: 'var(--reseau-ink-soft)' }}>
                            {p.body?.slice(0, 180)}{p.body?.length > 180 ? '…' : ''}
                        </p>
                    </article>
                ))}
            </div>
        </section>
    );
}

// ============================================================================
// Page principale
// ============================================================================
export default function PublicAgentProfilePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showIntent, setShowIntent] = useState(false);

    const loadProfile = useCallback(async () => {
        try {
            // Source principale : profil réseau enrichi
            const fullResponse = await api.get(`/reseau/profile/${slug}/full`).catch(() => null);
            if (fullResponse?.data) {
                setProfile(fullResponse.data);
            } else {
                const pub = await api.get(`/public/agent/${slug}`);
                setProfile(pub.data);
            }
            // Tracker non-bloquant
            api.post(`/profiles/public/${slug}/view`, { referrer: document.referrer || null }).catch(() => {});
        } catch (e) {
            toast.error('Profil introuvable');
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => { loadProfile(); }, [loadProfile]);

    useEffect(() => {
        if (profile?.full_name) document.title = `${profile.full_name} — Conseiller immobilier | Homly Réseau`;
        return () => { document.title = 'Homly'; };
    }, [profile]);

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            toast.success('Lien copié dans le presse-papier');
        } catch {
            toast.error('Impossible de copier le lien');
        }
    };

    if (loading) {
        return (
            <div data-reseau="light" className="reseau-light min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--reseau-violet)' }} />
            </div>
        );
    }

    if (!profile) {
        return (
            <div data-reseau="light" className="reseau-light min-h-screen flex items-center justify-center p-6 text-center">
                <div>
                    <p className="reseau-h2 text-xl mb-3">Profil introuvable</p>
                    <Link to="/reseau" className="reseau-btn reseau-btn-primary">Retour au réseau</Link>
                </div>
            </div>
        );
    }

    const firstName = profile.first_name || profile.full_name?.split(' ')[0] || '';
    const primaryZone = profile.zones?.[0] || profile.professional_zones?.[0]?.label;
    const positioning = profile.bio_long || profile.bio;

    return (
        <div data-reseau="light" className="reseau-light min-h-screen" style={{ background: 'var(--reseau-bg)' }}>
            {/* Navbar publique très sobre */}
            <header className="sticky top-0 z-30"
                    style={{ background: 'rgba(250, 250, 247, 0.92)', backdropFilter: 'blur(12px)',
                             borderBottom: '1px solid var(--reseau-border)' }}>
                <div className="mx-auto px-4 sm:px-6 flex items-center justify-between h-14"
                     style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                    <Link to="/reseau" className="flex items-center gap-2" data-testid="public-logo">
                        <span className="text-xl font-extrabold reseau-gradient-text"
                              style={{ fontFamily: 'var(--reseau-font-display)' }}>Homly</span>
                        <span style={{ color: 'var(--reseau-orange)' }}>.</span>
                        <span className="text-xs font-medium hidden sm:inline" style={{ color: 'var(--reseau-ink-muted)' }}>
                            Réseau
                        </span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <button onClick={handleShare} className="reseau-btn reseau-btn-ghost text-xs"
                                style={{ padding: '8px 12px' }} data-testid="share-btn">
                            <Share2 className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Partager</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* ── Hero (premier écran : humain, rassurant) ───────────────── */}
            <section className="border-b" style={{ background: 'var(--reseau-bg-soft)', borderColor: 'var(--reseau-border)' }}>
                <div className="mx-auto px-4 sm:px-6 py-8 sm:py-12" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                    <div className="flex flex-col sm:flex-row gap-5 sm:gap-8 items-start">
                        <AgentAvatar profile={profile} size="xl" />
                        <div className="flex-1 min-w-0">
                            {/* 1. Identité */}
                            <h1 className="reseau-h1 text-2xl sm:text-3xl lg:text-4xl mb-1"
                                data-testid="agent-name">
                                {profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`}
                            </h1>
                            {/* 2. Métier + agence */}
                            <p className="text-base font-medium mb-2" style={{ color: 'var(--reseau-ink-soft)' }}
                               data-testid="agent-role">
                                {profile.title_pro || 'Conseiller(e) immobilier'}
                            </p>
                            {profile.agency && (
                                <Link to={`/p/agency/${profile.agency.slug || profile.agency.id}`}
                                      className="inline-flex items-center gap-1.5 text-sm hover:underline mb-3"
                                      style={{ color: 'var(--reseau-violet)' }}
                                      data-testid="agency-link">
                                    <Building2 className="w-4 h-4" />
                                    {profile.agency.name}
                                </Link>
                            )}
                            {/* 3. Territoire principal */}
                            {primaryZone && (
                                <p className="text-sm mb-4 flex items-center gap-1.5" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    <MapPin className="w-4 h-4" />
                                    {primaryZone}
                                    {(profile.zones?.length > 1 || profile.professional_zones?.length > 1) && (
                                        <span className="text-xs">
                                            · +{(profile.zones?.length || profile.professional_zones?.length) - 1} autres zones
                                        </span>
                                    )}
                                </p>
                            )}
                            {/* 4. Phrase de positionnement (extrait court) */}
                            {positioning && (
                                <p className="text-sm sm:text-base leading-relaxed mb-5 max-w-2xl"
                                   style={{ color: 'var(--reseau-ink-soft)' }}
                                   data-testid="agent-positioning">
                                    {positioning.length > 220 ? positioning.slice(0, 220) + '…' : positioning}
                                </p>
                            )}
                            {/* 5. CTA principal unique + actions secondaires discrètes */}
                            <div className="flex flex-wrap items-center gap-2">
                                <button onClick={() => setShowIntent(true)}
                                        className="reseau-btn reseau-btn-primary"
                                        style={{ padding: '13px 22px', fontSize: 15 }}
                                        data-testid="primary-cta">
                                    <Sparkles className="w-4 h-4" />
                                    Présenter mon projet
                                </button>
                                <button onClick={handleShare}
                                        className="reseau-btn reseau-btn-ghost"
                                        data-testid="secondary-share">
                                    <Share2 className="w-4 h-4" />
                                    Partager
                                </button>
                                {profile.public_phone && (
                                    <a href={`tel:${profile.public_phone}`}
                                       className="reseau-btn reseau-btn-ghost"
                                       data-testid="secondary-call">
                                        <Phone className="w-4 h-4" />
                                        {profile.public_phone}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Corps : synthèse → avis → preuves → contenus → infos ─── */}
            <main className="mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5"
                  style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 lg:gap-6">
                    <div className="space-y-5">
                        {/* 5. Synthèse de confiance */}
                        <TrustSnapshot profileId={profile.id} onCtaClick={() => setShowIntent(true)} />

                        {/* 6. Avis vendeurs vérifiés */}
                        <PublicReviews profileId={profile.id} />

                        {/* 7. Preuves */}
                        <PublicProofs profileId={profile.id} />

                        {/* 8. Publications locales utiles — disponibles pour utilisateurs connectés via /reseau/fil */}
                    </div>

                    {/* ── Colonne secondaire : parcours, langues, spécialités ── */}
                    <aside className="space-y-4">
                        {profile.specialties?.length > 0 && (
                            <div className="reseau-card p-5">
                                <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider"
                                    style={{ color: 'var(--reseau-ink-muted)', letterSpacing: '0.05em' }}>
                                    Spécialités
                                </h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {profile.specialties.map((s, i) => (
                                        <span key={i} className="reseau-badge reseau-badge-violet text-xs">{s}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(profile.zones?.length > 0 || profile.professional_zones?.length > 0) && (
                            <div className="reseau-card p-5">
                                <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider"
                                    style={{ color: 'var(--reseau-ink-muted)', letterSpacing: '0.05em' }}>
                                    Zones travaillées
                                </h3>
                                <ul className="space-y-1.5 text-sm">
                                    {(profile.zones || profile.professional_zones?.map(z => z.label) || []).map((z, i) => (
                                        <li key={i} className="flex items-center gap-1.5" style={{ color: 'var(--reseau-ink-soft)' }}>
                                            <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--reseau-violet)' }} />
                                            {z}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {profile.languages?.length > 0 && (
                            <div className="reseau-card p-5">
                                <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider"
                                    style={{ color: 'var(--reseau-ink-muted)', letterSpacing: '0.05em' }}>
                                    Langues
                                </h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {profile.languages.map((l, i) => (
                                        <span key={i} className="reseau-badge reseau-badge-neutral text-xs">
                                            <Languages className="w-3 h-3 mr-1" />{l}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {profile.started_real_estate_at && (
                            <div className="reseau-card p-5">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                    <div>
                                        <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>Dans le métier depuis</p>
                                        <p className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                            {new Date(profile.started_real_estate_at).getFullYear()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Action signalement discret */}
                        <button onClick={() => toast.info('Pour signaler ce profil, contactez support@homly.pro')}
                                className="text-xs hover:underline mx-2"
                                style={{ color: 'var(--reseau-ink-muted)' }}
                                data-testid="report-link">
                            <Flag className="w-3 h-3 inline mr-1" />Signaler ce profil
                        </button>
                    </aside>
                </div>
            </main>

            {/* ── CTA sticky mobile (n'apparaît qu'en mobile, après scroll) ── */}
            <div className="lg:hidden sticky bottom-0 z-20 px-3 py-3 border-t"
                 style={{ background: 'rgba(250, 250, 247, 0.96)', backdropFilter: 'blur(12px)',
                          borderColor: 'var(--reseau-border)' }}>
                <button onClick={() => setShowIntent(true)}
                        className="reseau-btn reseau-btn-primary w-full"
                        style={{ padding: '13px 20px', fontSize: 15 }}
                        data-testid="sticky-cta-mobile">
                    <Sparkles className="w-4 h-4" />
                    Présenter mon projet à {firstName}
                </button>
            </div>

            {/* Footer */}
            <footer className="mt-8 border-t" style={{ borderColor: 'var(--reseau-border)', background: 'var(--reseau-surface)' }}>
                <div className="mx-auto px-4 sm:px-6 py-6 text-sm flex flex-col sm:flex-row items-center justify-between gap-3"
                     style={{ maxWidth: 'var(--reseau-maxw-page)', color: 'var(--reseau-ink-muted)' }}>
                    <span>Les avis Homly proviennent de mandats réels et sont modérés.</span>
                    <div className="flex items-center gap-4">
                        <Link to="/protection-des-donnees" className="hover:underline">Mes données</Link>
                        <Link to="/cgu" className="hover:underline">CGU</Link>
                        <Link to="/privacy" className="hover:underline">Confidentialité</Link>
                        <Link to="/reseau" className="hover:underline">Réseau Homly</Link>
                    </div>
                </div>
            </footer>

            <SellerIntentModal agent={profile}
                               open={showIntent}
                               onClose={() => setShowIntent(false)}
                               onSubmitted={() => {}} />
        </div>
    );
}
